import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Circle, Download, ArrowLeft, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { MOCK_COURSES } from '../utils/mockData';

function VideoPlayerPage() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourse();
    fetchProgress();
  }, [courseId]);

  const fetchCourse = () => {
    try {
      // Find course from mock data
      const foundCourse = MOCK_COURSES.find((c) => c.id === parseInt(courseId));
      
      if (!foundCourse) {
        toast.error('Course not found');
        setLoading(false);
        return;
      }

      setCourse(foundCourse);
      
      // Set first lesson as current if lessons exist
      if (foundCourse.lessons?.length > 0) {
        setCurrentLesson(foundCourse.lessons[0]);
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = () => {
    try {
      // Get progress from localStorage
      const savedProgress = localStorage.getItem(`course_progress_${courseId}`);
      if (savedProgress) {
        setProgress(JSON.parse(savedProgress));
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const markAsComplete = (lessonId) => {
    try {
      const newProgress = { ...progress, [lessonId]: true };
      setProgress(newProgress);
      
      // Save progress to localStorage
      localStorage.setItem(`course_progress_${courseId}`, JSON.stringify(newProgress));
      
      toast.success('Lesson marked as complete!');
    } catch (error) {
      console.error('Error marking lesson:', error);
      toast.error('Failed to mark lesson as complete');
    }
  };

  const downloadCertificate = () => {
    // Demo-only certificate download
    toast.success('Certificate downloaded! (Demo only)');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Course Not Found</h2>
          <Link to="/dashboard" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const completedLessons = Object.values(progress).filter(Boolean).length;
  const totalLessons = course.lessons?.length || 0;
  const progressPercentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  const isComplete = progressPercentage === 100;

  return (
    <div className="min-h-screen pt-24 bg-slate-50" data-testid="video-player-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Back Button */}
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 mb-6" data-testid="back-to-dashboard">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to My Courses</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video Player */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
              {currentLesson?.video_url ? (
                <div className="video-player">
                  <iframe
                    src={currentLesson.video_url}
                    className="w-full h-full"
                    allowFullScreen
                    title={currentLesson.title}
                    data-testid="video-iframe"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-slate-900 flex items-center justify-center">
                  <div className="text-center text-white">
                    <PlayCircle className="w-20 h-20 text-slate-600 mx-auto mb-4" />
                    <p className="text-lg">No video available for this lesson</p>
                  </div>
                </div>
              )}
            </div>

            {/* Lesson Info */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h1 className="text-3xl font-extrabold text-slate-900 mb-4" data-testid="lesson-title">{currentLesson?.title || 'Select a lesson'}</h1>
              <p className="text-slate-600 mb-6">{currentLesson?.description || 'No description available'}</p>
              {currentLesson && (
                <button
                  onClick={() => markAsComplete(currentLesson.order)}
                  disabled={progress[currentLesson.order]}
                  className={`btn-primary flex items-center gap-2 ${progress[currentLesson.order] ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="mark-complete-button"
                >
                  {progress[currentLesson.order] ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg sticky top-24">
              {/* Progress */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-slate-900">Course Progress</h3>
                  <span className="text-blue-600 font-bold">{progressPercentage}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3 mb-4">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <p className="text-sm text-slate-600">
                  {completedLessons} of {totalLessons} lessons completed
                </p>
                {isComplete && (
                  <button
                    onClick={downloadCertificate}
                    className="mt-4 w-full btn-secondary flex items-center justify-center gap-2"
                    data-testid="download-certificate-button"
                  >
                    <Download className="w-5 h-5" />
                    Download Certificate
                  </button>
                )}
              </div>

              {/* Lessons List */}
              <div className="p-6">
                <h3 className="font-bold text-slate-900 mb-4">Lessons</h3>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {course.lessons?.length > 0 ? (
                    course.lessons.map((lesson, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentLesson(lesson)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          currentLesson?.order === lesson.order
                            ? 'bg-blue-50 border-2 border-blue-600'
                            : 'hover:bg-slate-50 border-2 border-transparent'
                        }`}
                        data-testid={`lesson-item-${index}`}
                      >
                        <div className="flex items-start gap-3">
                          {progress[lesson.order] ? (
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Circle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">
                              {index + 1}. {lesson.title}
                            </div>
                            <div className="text-xs text-slate-600 mt-0.5">{lesson.duration} min</div>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">No lessons available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoPlayerPage;
